import React, { useEffect, useRef } from 'react';
import { FaTimes, FaStar, FaExternalLinkAlt } from 'react-icons/fa';
import FocusTrap from '../../utils/focusTrap';
import useScrollableDetection from '../../hooks/useScrollableDetection';
import './YelpModal.css';

const YelpModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const focusTrapRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isInteractingRef = useRef(false);
  
  // Hook to detect if modal content is scrollable
  const { ref: contentRef, isScrollable } = useScrollableDetection(isOpen);

  // Track interactions to prevent modal close
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e) => {
      const target = e.target;
      if (target.closest('button') || target.closest('.yelp-modal')) {
        isInteractingRef.current = true;
      }
    };

    const handleMouseUp = () => {
      setTimeout(() => {
        isInteractingRef.current = false;
      }, 100);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchstart', handleMouseDown);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isOpen]);

  // Focus trap and scroll lock management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Store the scroll position when modal opens
      scrollPositionRef.current = window.scrollY;
      
      // Create and activate focus trap
      focusTrapRef.current = new FocusTrap(modalRef.current);
      focusTrapRef.current.activate();
      
      // Simple scroll lock - just hide overflow
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent scroll events on the document, but allow scrolling inside modal
      const preventScroll = (e) => {
        // Allow scrolling inside the modal content
        if (modalRef.current && modalRef.current.contains(e.target)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });
      
      // Cleanup function
      return () => {
        // Remove event listeners
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);
        
        // Restore scroll
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollPositionRef.current);
        
        // Deactivate focus trap normally to restore focus
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
          focusTrapRef.current = null;
        }
      };
    }

    // Cleanup on unmount
    return () => {
      if (focusTrapRef.current) {
        focusTrapRef.current.destroy();
        focusTrapRef.current = null;
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleReviewClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOverlayClick = (e) => {
    // Don't close if user was interacting with modal content
    if (isInteractingRef.current) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };


  return (
    <div 
      className="yelp-modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="yelp-modal-title"
    >
      <div className="yelp-modal" ref={modalRef}>
        <button 
          className="yelp-modal-close" 
          onClick={onClose}
          aria-label="Close reviews modal"
        >
          <FaTimes />
        </button>
        
        <div 
          className={`yelp-modal-content ${isScrollable ? 'scrollable' : ''}`}
          ref={contentRef}
        >
          <div className="yelp-modal-form-card">
            <div className="reviews-intro pad-t-xs-5">
              <h3 id="yelp-modal-title" className="reviews-intro-title">Read Our Customer Reviews</h3>
              <p className="reviews-intro-text">
                See what our satisfied customers have to say about our work
              </p>
            </div>
            
            <div className="reviews-options">
            {/* Secondary Yelp Review Option */}
            <div className="review-option secondary">
              <div className="review-option-icon">
                <img 
                  src="/images/logos/Yelp-Review-Logo.png" 
                  alt="Yelp Reviews" 
                  className="review-platform-logo"
                />
              </div>
              <div className="review-option-content">
                <div className="review-option-header">
                  <h5 className="review-option-title">Additional Reviews</h5>
                  <div className="review-rating">
                    <span className="rating-stars">★★★★★</span>
                  </div>
                </div>
                
                <p className="review-option-description">
                Yelp showcases all of our genuine customer feedback without filtering, so we invite you to read our reviews directly through this link.
                </p>
                
                <button 
                  className="review-option-button secondary"
                  onClick={() => handleReviewClick('https://www.yelp.com/not_recommended_reviews/rc-roofing-detroit')}
                >
                  <span>View Additional Reviews</span>
                  <FaExternalLinkAlt className="external-link-icon" />
                </button>
              </div>
            </div>

            {/* Primary Yelp Review Option */}
            <div className="review-option primary">
              <div className="review-option-icon">
                <img 
                  src="/images/logos/Yelp-Review-Logo.png" 
                  alt="Yelp Reviews" 
                  className="review-platform-logo"
                />
              </div>
              <div className="review-option-content">
                <div className="review-option-header">
                  <h5 className="review-option-title">Main Yelp Reviews</h5>
                  <div className="review-rating">
                    <span className="rating-stars">★★★★★</span>
                  </div>
                </div>
                
                <p className="review-option-description">
                  View our complete collection of customer reviews and ratings on Yelp's main business page.
                </p>
                
                <button 
                  className="review-option-button primary"
                  onClick={() => handleReviewClick('https://www.yelp.com/biz/rc-roofing-detroit')}
                >
                  <span>View Main Reviews</span>
                  <FaExternalLinkAlt className="external-link-icon" />
                </button>
              </div>
            </div>
            </div>

            <div className="reviews-footer">
              <p className="reviews-footer-text">
                All reviews are from verified customers who have worked with RC Roofing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YelpModal;
